import { useState, createContext, useContext } from "react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, ComposedChart, Line } from "recharts";
import { Info, X, ExternalLink, TrendingUp, TrendingDown, Minus, Globe } from "lucide-react";
import ActionPlan from "./ActionPlan";

const ActionRefCtx = createContext({ finRef: null, businessName: "" });

const PALETTE = ["#b0902f", "#4a90d9", "#e87ea8", "#8b6fd0", "#e34b3a", "#2fae9e", "#e0a92e", "#1e7d5a"];
const money = (n) => (typeof n === "number" ? n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }) : "—");

function Inspector({ chart, onClose }) {
  if (!chart) return null;
  return (
    <div data-testid="chart-inspector" className="fixed inset-0 z-[80] flex justify-end bg-[#1e3d3a]/40 backdrop-blur-sm" onClick={onClose}>
      <aside className="h-full w-full max-w-lg overflow-y-auto bg-[#f4f1e8] p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#a3823f] font-bold">Behind this chart</p>
            <h3 className="mt-1 font-head text-xl text-[#1e3d3a]">{chart.title}</h3>
          </div>
          <button type="button" data-testid="chart-inspector-close" onClick={onClose} className="text-[#1e3d3a]/45 hover:text-[#1e3d3a] transition-colors duration-300"><X className="h-4 w-4" /></button>
        </div>

        {chart.focus && (
          <section data-testid="chart-inspector-focus" className="mt-5 rounded-2xl border border-[#c9a961]/60 bg-white p-4">
            <p className="text-[9px] uppercase tracking-[0.2em] text-[#a3823f]">You clicked</p>
            <div className="mt-1 flex items-baseline justify-between gap-3">
              <p className="font-head text-lg text-[#1e3d3a]">{chart.focus.label}</p>
              <p className="tabular-nums text-sm text-[#1e3d3a]">{chart.focus.value}</p>
            </div>
            {chart.focus.note && <p className="mt-2 text-xs leading-relaxed text-[#1e3d3a]/70">{chart.focus.note}</p>}
            {chart.focus.evidence?.length > 0 && (
              <div className="mt-3">
                <p className="text-[9px] uppercase tracking-[0.2em] text-[#1e3d3a]/45">{chart.focus.evidence.some((e) => e.real === false) ? "Feedback themes & reported complaints" : "Where this shows up in the documents"}</p>
                <div className="mt-1.5 space-y-1.5">
                  {chart.focus.evidence.map((e, i) => (
                    <div key={i} data-testid={`focus-evidence-${i}`} className="rounded-xl bg-[#f4f1e8] px-3 py-2">
                      {e.real === false ? (
                        <p className="text-xs text-[#1e3d3a]/85 flex items-center gap-1.5"><span className="inline-block h-1.5 w-1.5 rounded-full bg-[#a3823f]" />{e.text}</p>
                      ) : (
                        <p className="text-xs text-[#1e3d3a]/85">“{e.text}”</p>
                      )}
                      <p className="mt-1 text-[9px] uppercase tracking-[0.14em] text-[#1e3d3a]/40">{e.real === false ? "Feedback theme · to be verified" : e.source}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {chart.focus.links?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {chart.focus.links.map((l, i) => (
                  <a key={i} data-testid={`focus-link-${i}`} href={l.url} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded-full bg-[#1e3d3a] px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] text-white hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300">
                    <ExternalLink className="h-3 w-3" /> {l.label}
                  </a>
                ))}
              </div>
            )}
          </section>
        )}

        <section className="mt-5">
          <p className="text-[9px] uppercase tracking-[0.2em] text-[#1e3d3a]/45">The numbers</p>
          <table className="mt-2 w-full text-sm">
            <tbody>
              {chart.rows.map((r, i) => (
                <tr key={i} className="border-b border-[#1e3d3a]/8">
                  <td className="py-1.5 text-[#1e3d3a]/75">{r.label}</td>
                  <td className="py-1.5 text-right tabular-nums text-[#1e3d3a]">{r.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="mt-5">
          <p className="text-[9px] uppercase tracking-[0.2em] text-[#1e3d3a]/45">How it was derived</p>
          <p data-testid="chart-inspector-method" className="mt-2 text-sm leading-relaxed text-[#1e3d3a]/80 border-l-2 border-[#c9a961] pl-3">{chart.method || "Derived by the Elevare analyst model from the inputs below."}</p>
        </section>

        {chart.market && (chart.market.summary || (chart.market.trends || []).length > 0) && (
          <section data-testid="chart-inspector-market" className="mt-5 rounded-2xl border border-[#c9a961]/40 bg-[#c9a961]/8 p-4">
            <p className="text-[9px] uppercase tracking-[0.2em] text-[#a3823f]">Based on local market &amp; industry trend</p>
            {(chart.market.area || chart.market.industry) && (
              <p className="mt-1 text-[11px] font-medium text-[#1e3d3a]">{[chart.market.area, chart.market.industry].filter(Boolean).join(" · ")}</p>
            )}
            {chart.market.summary && <p className="mt-1.5 text-xs leading-relaxed text-[#1e3d3a]/75">{chart.market.summary}</p>}
            {(chart.market.trends || []).length > 0 && (
              <ul className="mt-3 space-y-2">
                {chart.market.trends.map((t, i) => {
                  const Icon = t.direction === "up" ? TrendingUp : t.direction === "down" ? TrendingDown : Minus;
                  const tone = t.direction === "up" ? "text-[#1e7d5a]" : t.direction === "down" ? "text-[#b23a48]" : "text-[#1e3d3a]/45";
                  return (
                    <li key={i} data-testid={`market-trend-${i}`} className="flex items-start gap-2 rounded-xl bg-white px-3 py-2">
                      <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${tone}`} />
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-[#1e3d3a]">{t.label}</p>
                        {t.detail && <p className="mt-0.5 text-[11px] leading-relaxed text-[#1e3d3a]/65">{t.detail}</p>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}

        {chart.inputs && (
          <section className="mt-5">
            <p className="text-[9px] uppercase tracking-[0.2em] text-[#1e3d3a]/45">Inputs that fed it</p>
            <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
              {Object.entries(chart.inputs).filter(([, v]) => v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && !v.length)).map(([k, v]) => (
                <div key={k} className="contents">
                  <dt className="text-[#1e3d3a]/50 capitalize">{k.replace(/_/g, " ").replace(/bbb/i, "BBB").replace(/sec /i, "SEC ")}</dt>
                  <dd className="text-[#1e3d3a] text-right">{Array.isArray(v) ? v.join(", ") : String(v)}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        <section className="mt-5">
          <p className="text-[9px] uppercase tracking-[0.2em] text-[#1e3d3a]/45">Sources</p>
          <ul className="mt-2 space-y-1.5">
            {(chart.sources || []).map((s, i) => (
              <li key={i} className="flex items-center justify-between gap-3 text-xs">
                <span className="text-[#1e3d3a]/80">
                  {s.label}
                  <span className={`ml-2 rounded-full px-1.5 py-0.5 text-[8px] uppercase tracking-[0.14em] ${s.kind === "real" ? "bg-[#4b7a3a]/12 text-[#4b7a3a]" : "bg-[#c9a961]/20 text-[#a3823f]"}`}>{s.kind === "real" ? "verified" : "analytical estimate"}</span>
                </span>
                {s.url && <a href={s.url} target="_blank" rel="noreferrer" className="shrink-0 text-[#a3823f] hover:text-[#1e3d3a]" title={s.url}><ExternalLink className="h-3.5 w-3.5" /></a>}
              </li>
            ))}
          </ul>
        </section>
        {chart.evidence?.length > 0 && (
          <section className="mt-5">
            <p className="text-[9px] uppercase tracking-[0.2em] text-[#1e3d3a]/45">Complaint text used as evidence</p>
            <div className="mt-2 space-y-2">
              {chart.evidence.map((e, i) => <p key={i} className="rounded-xl bg-white px-3 py-2 text-xs text-[#1e3d3a]/80">“{e}”</p>)}
            </div>
          </section>
        )}
      </aside>
    </div>
  );
}

const CONF_TONE = { High: "bg-[#4b7a3a]/12 text-[#4b7a3a]", Medium: "bg-[#c9a961]/20 text-[#a3823f]", Low: "bg-[#b23a48]/12 text-[#b23a48]" };
function ConfidenceTag({ level }) {
  if (!level) return null;
  const l = String(level);
  const key = l.charAt(0).toUpperCase() + l.slice(1).toLowerCase();
  return <span data-testid="confidence-tag" title="How firm this AI estimate is — based on how much real signal (BBB rating, complaints) backs it" className={`rounded-full px-2 py-0.5 text-[8px] uppercase tracking-[0.14em] whitespace-nowrap ${CONF_TONE[key] || CONF_TONE.Medium}`}>{key} confidence</span>;
}

function ChartCard({ testid, title, sub, onInspect, plan, confidence, children }) {
  return (
    <div className="flex flex-col">
    <div data-testid={testid} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onInspect()}
      onClick={(e) => { if (e.target?.closest?.(".recharts-sector, .recharts-rectangle, .recharts-pie-sector, .recharts-bar-rectangle")) return; onInspect(); }}
      className="group w-full cursor-pointer text-left rounded-2xl border border-[#1e3d3a]/12 bg-white p-5 hover:border-[#c9a961] transition-colors duration-300">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55">{title}</p>
          {sub && <p className="mt-1 text-[11px] text-[#1e3d3a]/50">{sub}</p>}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <ConfidenceTag level={confidence} />
          <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.16em] text-[#a3823f] opacity-70 group-hover:opacity-100"><Info className="h-3 w-3" /> Click for source</span>
        </div>
      </div>
      <div className="mt-3 h-52">{children}</div>
    </div>
    <ActionPlanWithRef plan={plan} testid={`${testid}-plan`} chartKey={testid} />
    </div>
  );
}

function ActionPlanWithRef({ plan, testid, chartKey }) {
  const { finRef, businessName } = useContext(ActionRefCtx);
  return <ActionPlan plan={plan} testid={testid} chartKey={chartKey} finRef={finRef} businessName={businessName} />;
}

export default function ProspectCharts({ fin, reported, bbbUrl, scopeLabel, finRef, businessName }) {
  const [open, setOpen] = useState(null);
  const f = fin?.financials || {};
  const prov = fin?.provenance || {};
  const METHOD_NOTE = "Elevare combines available business information, public signals, industry benchmarks, and analytical modeling to identify potential areas of opportunity. All preliminary findings are validated during the Elevare Diagnostic.";
  const plans = fin?.chart_actions || {};
  const conf = fin?.confidence || {};
  const sources = fin?.data_sources || [];
  const inputs = fin?.inputs_used || null;
  const complaintTexts = (reported?.reviews || []).map((r) => r.excerpt).filter(Boolean).slice(0, 6);

  const drivers = (f.loss_drivers || []).map((d, i) => ({ name: d.label, value: Number(d.amount) || 0, fill: PALETTE[i % PALETTE.length] }));
  const expenses = (f.expense_breakdown || []).map((c) => ({ name: c.label, Current: Math.round((Number(c.amount) || 0) / 12), "With Elevare": Math.round((Number(c.projected ?? (Number(c.amount) || 0) * 0.85)) / 12) }));
  const neg = (fin?.reviews?.negative || []).length + (reported?.reviews || []).length;
  const pos = (fin?.reviews?.positive || []).length;
  const reviews = [{ name: "Positive", value: pos, fill: "#4b7a3a" }, { name: "Negative", value: neg, fill: "#b23a48" }];

  const survival = fin?.survival_risk || null;
  const expansion = fin?.expansion || null;
  const survTraj = (survival?.trajectory || []).map((t) => ({ name: t.year, "If nothing changes": Number(t.risk_without) || 0, "With Elevare": Number(t.risk_with) || 0 }));
  const survFactors = (survival?.factors || []).map((x) => ({ label: x.label, value: Number(x.weight) || 0, note: x.note }));
  const expProj = (expansion?.projection || []).map((p) => ({ name: p.name, Revenue: Number(p.revenue) || 0, Profit: Number(p.profit) || 0 }));
  const wa = fin?.web_analytics || null;
  const hasWeb = !!(wa && wa.has_website);
  const waAudience = (wa?.audience || []).map((x, i) => ({ name: x.label, value: Number(x.pct) || 0, fill: PALETTE[i % PALETTE.length] }));
  const waGeo = (wa?.geography || []).map((x) => ({ name: x.location, value: Number(x.pct) || 0 }));
  const DEVICE_FILL = { Mobile: "#1e7d5a", Desktop: "#4a90d9", Tablet: "#c9a961" };
  const waDevices = (wa?.devices || []).map((x) => ({ name: x.label, value: Number(x.pct) || 0, fill: DEVICE_FILL[x.label] || "#8b6fd0" }));
  const waSources = (wa?.sources || []).map((x, i) => ({ name: x.label, value: Number(x.pct) || 0, fill: PALETTE[i % PALETTE.length] }));
  const waRows = (arr) => arr.map((d) => ({ label: d.name, value: `${d.value}%` }));

  if (!drivers.length && !expenses.length) return null;

  const base = { inputs, sources, evidence: complaintTexts, market: fin?.market_context || null };
  const bbbLinks = bbbUrl ? [
    { label: "BBB complaints", url: bbbUrl.replace(/\/+$/, "") + "/complaints" },
    { label: "BBB customer reviews", url: bbbUrl.replace(/\/+$/, "") + "/customer-reviews" },
    { label: "BBB profile", url: bbbUrl },
  ] : [];
  const STOP = new Set(["and", "the", "with", "from", "into", "that", "this", "for", "low", "bad", "poor", "related", "costs", "fees", "cost"]);
  const kw = (label) => (label || "").toLowerCase().split(/[^a-z]+/).filter((w) => w.length >= 4 && !STOP.has(w));
  const STEMS = { refund: ["refund", "money back", "reimburs"], chargeback: ["chargeback", "dispute", "charged twice", "double"], booking: ["booking", "reservation", "reserved", "front desk", "check-in", "check in"], review: ["review", "rating", "star"], vacancy: ["review", "never again", "won't return", "will not return"], condition: ["dirty", "filthy", "clean", "mold", "broken", "condition", "smell"], billing: ["charge", "bill", "fee", "invoice"], payroll: ["staff", "employee", "manager"], marketing: ["ad", "promot", "advertis"], ota: ["expedia", "booking.com", "priceline", "hotels.com", "third party"], repair: ["repair", "fix", "broken", "warranty"], deposit: ["deposit"], delivery: ["deliver", "shipped", "arrive"], quality: ["quality", "defect", "damage"] };
  const allComplaints = [
    ...(reported?.reviews || []).map((r) => ({ text: r.excerpt, source: r.source || "BBB · reported complaint", real: true })),
    ...(fin?.reviews?.negative || []).map((r) => ({ text: r.theme || "Recurring complaint", source: "Feedback theme", real: false })),
  ].filter((e) => e.text);
  const matchEvidence = (label) => {
    const words = kw(label);
    const needles = new Set(words);
    words.forEach((w) => Object.entries(STEMS).forEach(([k, arr]) => { if (w.startsWith(k) || k.startsWith(w)) arr.forEach((n) => needles.add(n)); }));
    const hits = allComplaints.filter((e) => { const t = e.text.toLowerCase(); return [...needles].some((n) => t.includes(n)); });
    return (hits.length ? hits : allComplaints.slice(0, 3)).slice(0, 5);
  };
  const flagsFor = (label) => (fin?.fraud_risk?.flags || []).filter((fl) => kw(label).some((w) => `${fl.pattern} ${fl.evidence}`.toLowerCase().includes(w)));
  // recharts passes (data, index, event) — but be tolerant of any order.
  const pick = (args) => {
    const d = args.find((a) => a && typeof a === "object" && !a.nativeEvent && (a.payload !== undefined || a.name !== undefined || a.value !== undefined));
    return d?.payload && typeof d.payload === "object" ? { ...d.payload, ...d } : d || {};
  };

  const openDriver = (name, value) => {
    const fl = flagsFor(name);
    setOpen({ ...base, title: "Where it leaks · loss drivers", method: METHOD_NOTE, rows: drivers.map((d) => ({ label: d.name, value: money(d.value) })),
      focus: { label: name, value: `${money(value)} / yr`, evidence: matchEvidence(name),
        note: fl.length ? `Also flagged in the fraud scan: ${fl.map((x) => x.pattern).join(", ")}.` : "Sized by the analyst model from the complaint themes below and typical ticket sizes for this industry.",
        links: [...bbbLinks, ...fl.filter((x) => x.source_url).map((x) => ({ label: `Flag source · ${x.pattern}`, url: x.source_url }))] } });
  };
  const openExpense = (row) => {
    setOpen({ ...base, title: "Expenses · with vs without Elevare", method: METHOD_NOTE, rows: expenses.map((e) => ({ label: e.name, value: `${money(e.Current)} → ${money(e["With Elevare"])}` })),
      focus: { label: row.name, value: `${money(row.Current)} → ${money(row["With Elevare"])} / mo`, evidence: matchEvidence(row.name),
        note: `Saving ≈ ${money(Math.max(0, row.Current - row["With Elevare"]))} per month. Current spend is an industry-benchmark estimate; the "with Elevare" figure applies the roadmap's cut for this category.`, links: bbbLinks } });
  };
  const loss = Number(f.reported_profit_loss) || 0;
  const rec = Number(f.recoverable_with_elevare) || 0;
  const RAMP = [0.2, 0.45, 0.75, 1.0];
  const phases = fin?.recovery_plan || [];
  const swing = [
    { name: "Today", "Still leaking": loss, Recovered: 0, Trajectory: 0, "Net swing": -loss, q: null },
    ...RAMP.map((r, i) => ({ name: `Q${i + 1} goal`, "Still leaking": Math.round(loss * (1 - r)), Recovered: Math.round(rec * r), Trajectory: Math.round(rec * r), "Net swing": Math.round(rec * r - loss * (1 - r)), q: i })),
  ];
  const swingRows = swing.map((d) => ({ label: d.name, value: `${money(d["Still leaking"])} leaking · ${money(d.Recovered)} recovered` }));
  const openSwing = (row) => {
    const ph = row.q != null ? phases[Math.min(row.q, phases.length - 1)] : null;
    const isToday = row.q == null;
    setOpen({ ...base, title: "Profit swing · quarterly goals", method: METHOD_NOTE, rows: swingRows,
      focus: {
        label: row.name, value: isToday ? `${money(loss)} leaking / yr` : `${money(row.Recovered)} recovered · ${money(row["Still leaking"])} still leaking · net ${row["Net swing"] >= 0 ? "+" : ""}${money(row["Net swing"])}`,
        evidence: isToday ? allComplaints.slice(0, 4) : [],
        note: isToday
          ? "The leak is the sum of the loss drivers, each traced to complaint themes."
          : `Goal: reach ${Math.round(RAMP[row.q] * 100)}% of the recoverable amount by the end of Q${row.q + 1}.` + (ph ? ` Roadmap phase “${ph.phase}”${ph.timeframe ? ` (${ph.timeframe})` : ""}: ${ph.objective || ""}${ph.milestone ? ` Milestone — ${ph.milestone}.` : ""}` : ""),
        links: bbbLinks } });
  };
  const openReview = (name, value) => {
    const list = name === "Negative" ? allComplaints : (fin?.reviews?.positive || []).map((r) => ({ text: r.theme || "Positive feedback", source: "Feedback theme", real: false }));
    setOpen({ ...base, title: "Reviews · sentiment mix", method: METHOD_NOTE, rows: reviews.map((r) => ({ label: r.name, value: String(r.value) })),
      focus: { label: `${name} reviews`, value: String(value), evidence: list.slice(0, 6), note: name === "Negative" ? "Reported BBB complaints are quoted as filed; other items are shown as feedback themes, to be confirmed during the diagnostic." : "Shown as feedback themes from public signals — to be confirmed against real reviews during the diagnostic.", links: bbbLinks } });
  };
  const openSurvival = (focus) => setOpen({ ...base, title: "Survival risk · will they still be open?", method: survival?.summary,
    rows: [
      { label: `Closure risk (next ${survival?.horizon_years || 3} yrs)`, value: `${survival?.closure_probability_pct ?? survival?.score ?? 0}%` },
      ...survFactors.map((x) => ({ label: x.label, value: `${x.value}% of risk` })),
    ], focus, links: bbbLinks });
  const openExpansion = (focus) => setOpen({ ...base, title: "Expansion · financial upside", method: expansion?.summary,
    rows: [
      { label: "Readiness", value: `${expansion?.readiness_score ?? 0}/100 · ${expansion?.level || "—"}` },
      ...(expansion?.investment_required ? [{ label: "Est. investment to fund it", value: money(expansion.investment_required) }] : []),
      ...(expansion?.opportunities || []).map((o) => ({ label: o.label, value: o.est_upside ? `+${money(o.est_upside)}/yr` : "—" })),
    ], focus });
  const alarm = rec > 0
    ? { big: `${money(rec)} in annual profit improvement identified`, sub: "Estimated annual opportunity Elevare can help this business capture — confirmed together in the diagnostic." }
    : (loss > 0 ? { big: `${money(loss)} in annual profit opportunity`, sub: "Profit currently left on the table through refunds, complaints and inefficiency." } : null);
  return (
    <ActionRefCtx.Provider value={{ finRef: finRef || null, businessName: businessName || "" }}>
    <div data-testid="prospect-charts" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {scopeLabel && <p className="lg:col-span-2 text-[10px] uppercase tracking-[0.2em] text-[#a3823f]">Showing: {scopeLabel}</p>}
      {alarm && (
        <div data-testid="prospect-alarm" className="lg:col-span-2 rounded-2xl border border-[#1e7d5a]/40 bg-[#1e7d5a]/[0.06] px-6 py-5">
          <p className="text-[9px] uppercase tracking-[0.26em] text-[#1e7d5a] font-bold">Estimated annual opportunity</p>
          <p className="mt-1.5 font-head text-3xl sm:text-4xl lg:text-5xl text-[#1e7d5a] leading-[1.05]">{alarm.big}</p>
          <p className="mt-2 text-sm text-[#1e3d3a]/70">{alarm.sub}</p>
        </div>
      )}
      {drivers.length > 0 && (
        <ChartCard testid="chart-loss-drivers" confidence={conf.financials} plan={plans.loss_drivers} title="Where it leaks · loss drivers" sub={`Est. ${money(f.reported_profit_loss)} / yr`}
          onInspect={() => setOpen({ ...base, title: "Where it leaks · loss drivers", method: METHOD_NOTE, rows: drivers.map((d) => ({ label: d.name, value: money(d.value) })) })}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={drivers} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2} stroke="#f4f1e8" isAnimationActive={false}
                onClick={(...a) => { const d = pick(a); openDriver(d.name, d.value); }} className="cursor-pointer">
                {drivers.map((d, i) => <Cell key={i} fill={d.fill} data-testid={`slice-loss-${i}`} />)}
              </Pie>
              <Tooltip formatter={(v) => money(v)} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      )}
      {expenses.length > 0 && (
        <ChartCard testid="chart-expenses" confidence={conf.financials} plan={plans.expenses} title="Expenses · with vs without Elevare" sub="Monthly, by category"
          onInspect={() => setOpen({ ...base, title: "Expenses · with vs without Elevare", method: METHOD_NOTE, rows: expenses.map((e) => ({ label: e.name, value: `${money(e.Current)} → ${money(e["With Elevare"])}` })) })}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={expenses} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
              <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} tickFormatter={(s) => (s.length > 12 ? s.slice(0, 11) + "…" : s)} />
              <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} width={38} />
              <Tooltip formatter={(v) => money(v)} />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="Current" fill="#b23a48" radius={[3, 3, 0, 0]} isAnimationActive={false} className="cursor-pointer" onClick={(...a) => openExpense(pick(a))} />
              <Bar dataKey="With Elevare" fill="#1e7d5a" radius={[3, 3, 0, 0]} isAnimationActive={false} className="cursor-pointer" onClick={(...a) => openExpense(pick(a))} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}
      <ChartCard testid="chart-profit-swing" confidence={conf.financials} plan={plans.profit_swing} title="Profit swing · quarterly goals" sub={`Recover ≈ ${money(rec)} / yr by Q4 · ${RAMP.map((r, i) => `Q${i + 1} ${Math.round(r * 100)}%`).join(" · ")}`}
        onInspect={() => setOpen({ ...base, title: "Profit swing · quarterly goals", method: prov.recovery, rows: [...swingRows, { label: "Est. revenue / yr", value: money(f.estimated_annual_revenue) }, { label: "Est. net profit / yr", value: money(f.estimated_net_profit) }] })}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={swing} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} />
            <YAxis domain={[0, "auto"]} allowDataOverflow={false} tick={{ fontSize: 9 }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} width={42} />
            <Tooltip formatter={(v) => money(v)} />
            <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
            <Bar dataKey="Still leaking" fill="#b23a48" radius={[3, 3, 0, 0]} isAnimationActive={false} className="cursor-pointer" onClick={(...a) => openSwing(pick(a))} />
            <Bar dataKey="Recovered" fill="#1e7d5a" radius={[3, 3, 0, 0]} isAnimationActive={false} className="cursor-pointer" onClick={(...a) => openSwing(pick(a))} />
            <Line type="monotone" dataKey="Trajectory" name="Recovery trajectory" stroke="#c9a961" strokeWidth={2} dot={{ r: 3, fill: "#c9a961" }} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>
      <ChartCard testid="chart-reviews" confidence={conf.reviews || conf.financials} plan={plans.reviews} title="Reviews · sentiment mix" sub={`${pos} positive · ${neg} negative`}
        onInspect={() => setOpen({ ...base, title: "Reviews · sentiment mix", method: METHOD_NOTE, rows: reviews.map((r) => ({ label: r.name, value: String(r.value) })), sources: [...(bbbUrl ? [{ label: "BBB profile & complaints", url: bbbUrl, kind: "real" }] : []), { label: "Review ledger (Google/Yelp/Facebook)", url: null, kind: "estimate" }] })}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={reviews} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2} stroke="#f4f1e8" isAnimationActive={false}
              onClick={(...a) => { const d = pick(a); openReview(d.name, d.value); }} className="cursor-pointer">
              {reviews.map((d, i) => <Cell key={i} fill={d.fill} data-testid={`slice-review-${i}`} />)}
            </Pie>
            <Tooltip />
            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 10 }} />
          </PieChart>
        </ResponsiveContainer>
      </ChartCard>
      {survival && survTraj.length > 0 && (
        <ChartCard testid="chart-survival-risk" confidence={conf.survival} plan={plans.survival}
          title="Survival risk · will they still be open?"
          sub={`${survival.level || "—"} · ${survival.closure_probability_pct ?? survival.score ?? 0}% closure risk in ${survival.horizon_years || 3} yrs`}
          onInspect={() => openSurvival(null)}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={survTraj} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 9 }} tickFormatter={(v) => `${v}%`} width={34} />
              <Tooltip formatter={(v) => `${v}% risk`} />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="If nothing changes" fill="#b23a48" radius={[3, 3, 0, 0]} isAnimationActive={false} className="cursor-pointer"
                onClick={(...a) => { const d = pick(a); openSurvival({ label: d.name, value: `${d["If nothing changes"]}% risk if nothing changes → ${d["With Elevare"]}% with Elevare`, note: "Closure / bankruptcy risk climbs every year the leak and reputation damage go unaddressed. The green line is what the prevention plan below buys them." }); }} />
              <Line type="monotone" dataKey="With Elevare" stroke="#1e7d5a" strokeWidth={2} dot={{ r: 3, fill: "#1e7d5a" }} isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>
      )}
      {expansion && expProj.length > 0 && (
        <ChartCard testid="chart-expansion" confidence={conf.expansion} plan={plans.expansion}
          title="Expansion · financial upside"
          sub={`${expansion.level || "—"} · readiness ${expansion.readiness_score ?? 0}/100${expansion.investment_required ? ` · ${money(expansion.investment_required)} to fund` : ""}`}
          onInspect={() => openExpansion(null)}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={expProj} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} />
              <YAxis domain={[0, "auto"]} allowDataOverflow tick={{ fontSize: 9 }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} width={42} />
              <Tooltip formatter={(v) => money(v)} />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="Revenue" fill="#c9a961" radius={[3, 3, 0, 0]} isAnimationActive={false} className="cursor-pointer"
                onClick={(...a) => { const d = pick(a); openExpansion({ label: d.name, value: `${money(d.Revenue)} revenue · ${money(d.Profit)} profit`, note: "Projected if the business expands with Elevare after stabilizing. 'Today' is the current estimate; later years assume the expansion plan below is executed." }); }} />
              <Line type="monotone" dataKey="Profit" stroke="#1e7d5a" strokeWidth={2} dot={{ r: 3, fill: "#1e7d5a" }} isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>
      )}
      {hasWeb && (
        <>
          <div className="lg:col-span-2 mt-2 flex items-center justify-between gap-3 flex-wrap border-t border-[#1e3d3a]/10 pt-4">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#a3823f] flex items-center gap-1.5">
              <Globe className="h-3.5 w-3.5 text-[#c9a961]" /> Website analytics · {wa.website || "who's searching them"}
            </p>
            <p className="text-[11px] text-[#1e3d3a]/55">
              ~{Number(wa.monthly_visits || 0).toLocaleString()} visits/mo{typeof wa.trend_pct === "number" ? ` · ${wa.trend_pct >= 0 ? "+" : ""}${wa.trend_pct}% MoM` : ""} · Analytical estimate
            </p>
          </div>
          {waAudience.length > 0 && (
            <ChartCard testid="chart-web-audience" confidence={conf.web} title="Who's searching · audience" sub={`~${Number(wa.monthly_unique_visitors || wa.monthly_visits || 0).toLocaleString()} unique visitors / mo`}
              onInspect={() => setOpen({ ...base, title: "Who's searching · audience", method: wa.summary, rows: waRows(waAudience) })}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={waAudience} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2} stroke="#f4f1e8" isAnimationActive={false}
                    onClick={(...a) => { const d = pick(a); setOpen({ ...base, title: "Who's searching · audience", method: wa.summary, rows: waRows(waAudience), focus: { label: d.name, value: `${d.value}% of visitors`, note: "Estimated share of the site's searchers with this intent — grounded in the local area and industry." } }); }} className="cursor-pointer">
                    {waAudience.map((d, i) => <Cell key={i} fill={d.fill} data-testid={`slice-web-audience-${i}`} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `${v}%`} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
          {waGeo.length > 0 && (
            <ChartCard testid="chart-web-geo" confidence={conf.web} title="Where they're searching from" sub="Share of visits by location"
              onInspect={() => setOpen({ ...base, title: "Where they're searching from", method: wa.summary, rows: waRows(waGeo) })}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={waGeo} layout="vertical" margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                  <XAxis type="number" domain={[0, "auto"]} tick={{ fontSize: 9 }} tickFormatter={(v) => `${v}%`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={92} tickFormatter={(s) => (s.length > 16 ? s.slice(0, 15) + "…" : s)} />
                  <Tooltip formatter={(v) => `${v}%`} />
                  <Bar dataKey="value" name="Share of visits" fill="#4a90d9" radius={[0, 3, 3, 0]} isAnimationActive={false} className="cursor-pointer"
                    onClick={(...a) => { const d = pick(a); setOpen({ ...base, title: "Where they're searching from", method: wa.summary, rows: waRows(waGeo), focus: { label: d.name, value: `${d.value}% of visits`, note: "Estimated share of searches/visits originating from this area — mostly local for a local business." } }); }} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
          {waDevices.length > 0 && (
            <ChartCard testid="chart-web-devices" confidence={conf.web} title="Phone vs computer · devices" sub="Share of visits by device"
              onInspect={() => setOpen({ ...base, title: "Phone vs computer · devices", method: wa.summary, rows: waRows(waDevices) })}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={waDevices} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2} stroke="#f4f1e8" isAnimationActive={false}
                    onClick={(...a) => { const d = pick(a); setOpen({ ...base, title: "Phone vs computer · devices", method: wa.summary, rows: waRows(waDevices), focus: { label: d.name, value: `${d.value}% of visits`, note: "Estimated share of visits on this device type. Local searches usually skew heavily to mobile." } }); }} className="cursor-pointer">
                    {waDevices.map((d, i) => <Cell key={i} fill={d.fill} data-testid={`slice-web-device-${i}`} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `${v}%`} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
          {waSources.length > 0 && (
            <ChartCard testid="chart-web-sources" confidence={conf.web} title="Traffic sources · where visits come from" sub="Organic · Maps · Social · Direct (%)"
              onInspect={() => setOpen({ ...base, title: "Traffic sources · where visits come from", method: wa.summary, rows: waRows(waSources) })}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={waSources} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2} stroke="#f4f1e8" isAnimationActive={false}
                    onClick={(...a) => { const d = pick(a); setOpen({ ...base, title: "Traffic sources · where visits come from", method: wa.summary, rows: waRows(waSources), focus: { label: d.name, value: `${d.value}% of visits`, note: "Estimated share of visits arriving from this channel — for a local business, organic search and Google Maps / local usually lead." } }); }} className="cursor-pointer">
                    {waSources.map((d, i) => <Cell key={i} fill={d.fill} data-testid={`slice-web-source-${i}`} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `${v}%`} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </>
      )}
      <Inspector chart={open} onClose={() => setOpen(null)} />
    </div>
    </ActionRefCtx.Provider>
  );
}
